import React, { useContext, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { App, Button, Col, Divider, Form, Input, InputRef, Modal, Row, Spin, Typography } from 'antd'
import { useIntl } from 'react-intl'
import qs from 'qs'
import * as Sentry from '@sentry/react'
import axios from 'axios'
import omit from 'lodash/omit'
import { ClientContext, useManualQuery } from 'graphql-hooks'
import { getCompanyAndUserInfo } from '../../graphql/custom-queries'

import { MicrosoftAuth } from '../../services/auth/microsoft/microsoft'
import { SlackAuth } from '../../services/auth/slack/slack'
import { getUserId as getSlackUserId, updateSlackToken } from '../../services/api/slack'
import { getUserId as getMsUserId } from '../../services/api/microsoft'
import { getUserId as getGoogleUserId } from '../../services/api/google'
import { GoogleAuth } from '../../services/auth/google/google'
import { track } from '../../services/analytics/analytics'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { setUserId } from '../../store/user-id-slice'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { selectAuthStateSlice, setAuthState } from '../../store/auth-state-slice'
import { SLACK_REQUIRED_BOT_SCOPES, SLACK_REQUIRED_USER_SCOPES } from '@vacationtracker/shared/data/slack'

import IntlMessages from '../../util/IntlMessages'
import CompanyAlreadySignedUpModal from '../../components/company-already-signed-up-modal'
import { SeoTags } from '../../components/seo-tags'
import { TeamsIcon, GoogleIcon, SlackIcon } from '../../components/icons'
import MobileApp from './components/mobile-app'

import { IGetCompanyAndUserInfo } from '../../types/custom-queries'
import { ICheckUserId, SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'

import { ISignin } from './types'
import { FrontendUrls } from '../../types/urls'
import { getDashboardUrl } from '../../util/get-dashboard-url'
import { FieldData } from 'rc-field-form/lib/interface'
import { Platform } from '@vacationtracker/shared/types/core-event'
import DontHaveAnAccountModal from '../../components/dont-have-an-account-modal'
import { setCrispSessionInfo } from '../../util/set-crisp-session-info'
import { CrispSegmentsEnum } from '../../types/crisp'
import { IContactAdminData } from '../../types/auth'
import CompanyExistsModal from '../../components/modal-company-exists'
import ForgotPasswordModal from '../../components/modal-forgot-password'
import MFACodeModal from '../../components/mfa-code-modal'
import { getCognitoErrors } from '../../components/mfa-code/errors'
import { ISlackOpenidTokenResponse } from '@vacationtracker/shared/types/repository/slack-api-repository'
import { confirmSigninWithCustomChallengeAnswer, getAuthTokens, IAuthNextStep, IAuthUser, signin } from '@vacationtracker/shared/services/auth'
import { AuthStateEnum } from '@vacationtracker/shared/types/auth-state'
import { getCompanyAndUser } from '../../services/api/companies'
import * as logger from '../../services/logger'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { BrowserRuntime } from '../../util/openid/browser-runtime'

const { Paragraph, Title, Link } = Typography

if (!process.env.REACT_APP_MICROSOFT_CLIENT_ID || !process.env.REACT_APP_SLACK_CLIENT_ID || !process.env.REACT_APP_GOOGLE_CLIENT_ID) {
  throw new Error('Client ID are required')
}

const msAuth = new MicrosoftAuth(process.env.REACT_APP_MICROSOFT_CLIENT_ID)
const slackAuth = new SlackAuth(process.env.REACT_APP_SLACK_CLIENT_ID)
const googleAuth = new GoogleAuth(process.env.REACT_APP_GOOGLE_CLIENT_ID)

const Signin = ({ location }: ISignin): React.ReactElement => {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const query = new URLSearchParams(location.search)
  const [form] = Form.useForm()
  const dispatch = useAppDispatch()
  const gqlClient = useContext(ClientContext)
  const { notification } = App.useApp()

  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { authState } = useAppSelector(selectAuthStateSlice)
  const [redirectUrl, setRedirectUrl] = useState<string | FrontendUrls>(getDashboardUrl())
  const [focusedPlatform, setFocusedPlatform] = useState<Platform | null>(null)
  const [userEmail, setUserEmail] = useState<string | null>(null)
  const [userWorkspaceName, setUserWorkspaceName] = useState('')
  const [signinInProgress, setSigninInProgress] = useState(false)
  const [showSlackLoginRequiredModal, setShowSlackLoginRequiredModal] = useState(false)
  const [companyExists, setCompanyExists] = useState(false)
  const [contactAdminModal, setContactAdminModal] = useState<IContactAdminData | null>(null)
  const [createAccountModalVisible, showCreateAccountModal] = useState(false)
  const [existsOnAnotherPlatform, setExistsOnAnotherPlatform] = useState('')
  const [companyAlreadySignedUpShowModal, setCompanyAlreadySignedUpShowModal] = useState(false)
  const [username, setUsername] = useState<string | null>(null)
  const [forgotPasswordModalVisible, showForgotPasswordModal] = useState(false)
  const [authInfoAbortController, setAuthInfoAbortController] = useState<AbortController | null>(null)
  const [isCheckingEmail, setIsCheckingEmail] = useState(false)
  const [passwordRequired, setPasswordRequired] = useState(false)
  const [showMFAModal, setShowMFAModal] = useState<boolean>(false)
  const [slackTeamId, setSlackTeamId] = useState<string | null>(null)
  const [adpLoginEnabled, setAdpLoginEnabled] = useState<boolean>(false)
  const [showPasswordInput, setShowPasswordInput] = useState(false)

  const [getCompanyAndUserInfoQuery] = useManualQuery<IGetCompanyAndUserInfo, { userId: string }>(getCompanyAndUserInfo)

  const emailInputRef = useRef<InputRef | null>(null)
  const passwordInputRef = useRef<InputRef | null>(null)

  const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true })

  useEffect(() => {
    if (authState === AuthStateEnum.signedIn) {
      goToDashboard(redirectUrl)
    }
  }, [authState])

  // Handle ADP auth code in popup window
  useEffect(() => {
    // If redirected back with a code and this window has an opener, return the code to the opener and close
    if (queryParams?.code && (typeof window !== 'undefined') && window.opener) {
      BrowserRuntime().postOpenerMessage('adp-auth', queryParams.code)
      window.close()
      return
    }

    // Normal signin page initialization
    if (queryParams?.redirect) {
      setRedirectUrl(queryParams.redirect as string)
    }
    if (queryParams?.platform) {
      setFocusedPlatform(queryParams.platform as Platform)
    }

    const preselectedSubscriptionPlan = queryParams?.subscriptionPlan === 'Free' ? SubscriptionPlanEnum.freemium : queryParams?.subscriptionPlan as SubscriptionPlanEnum | null

    if (
      preselectedSubscriptionPlan
      && [SubscriptionPlanEnum.freemium, SubscriptionPlanEnum.core, SubscriptionPlanEnum.complete].includes(preselectedSubscriptionPlan)
    ) {
      localStorage.setItem('vtSubscriptionPlan', preselectedSubscriptionPlan)
    }

    if (queryParams?.forgotpassword) {
      showForgotPasswordModal(true)
    }

    if (authUser.id && authState === AuthStateEnum.signedIn) {
      const dashboardUrl = getDashboardUrl(authUser.role)
      history.push(queryParams?.redirect as string || dashboardUrl)
    } else {
      history.push(FrontendUrls.signin + location.search)
    }
  }, [authState, authUser])

  useEffect(() => {
    track('CONNECT_PAGE_VIEWED', {})
    document.body.classList.remove('flow-b-theme')
    localStorage.removeItem('vtCreateUser')
    localStorage.removeItem('vtCreateCompany')
    localStorage.removeItem('vtSelectedUsers')
    if (queryParams?.error === 'subscription_expired') {
      // Subscription expired
      notification.error({
        message: formatMessage({ id: 'connect.subscriptionExpiredTitle' }),
        description: formatMessage({ id: 'error.subscriptionExpired' }),
        duration: 0,
      })
    }
    setCrispSessionInfo(
      [CrispSegmentsEnum.chat, CrispSegmentsEnum.support, CrispSegmentsEnum.dashboard, CrispSegmentsEnum.onLoginPage],
      { loginScreen: ''}
    )
  }, [])

  const getUsersByEmail = async (email: string) => {
    setIsCheckingEmail(true)
    if (authInfoAbortController) {
      authInfoAbortController.abort()
    }
    try {
      await form.validateFields()
      const controller = new AbortController()
      setAuthInfoAbortController(controller)
      notification.destroy('USER_INACTIVE')
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/core/auth-info`, {
        email,
      }, {
        signal: controller.signal,
      })
      setUsername(res.data.username)
      setAdpLoginEnabled(res.data?.loginOptions?.includes('adp'))
      const platform = res.data.platform
      if (platform !== PlatformEnum.Email) {
        setExistsOnAnotherPlatform(platform)
        setCompanyAlreadySignedUpShowModal(true)
      } else {
        setShowPasswordInput(true)
      }
      setAuthInfoAbortController(null)
      setIsCheckingEmail(false)
    } catch(err) {
      if (err.errorFields?.length === 0 && err.outOfDate === true) {
        setIsCheckingEmail(false)
        return
      }
      if (err?.response?.data?.code === 'EmailPlatformUserInactive') {
        notification.error({
          message: formatMessage({ id: 'error.signin.inactiveUser' }),
          key: 'USER_INACTIVE',
          description: formatMessage({ id: 'error.auth.companyExists.line1' }),
          duration: 0,
        })
        return
      }
      if (err?.response?.data?.code === 'UserNotFound') {
        history.push(FrontendUrls.signup)
        return
      }
      if (err?.response?.data?.code === 'EmailNotValid') {
        notification.error({
          message: formatMessage({ id: 'error.emailError' }),
          description: formatMessage({ id: 'error.emailError' }),
          duration: 0,
        })
        history.push(FrontendUrls.signup)
        return
      }
      if (err.message !== 'canceled') {
        setIsCheckingEmail(false)
      }
      setUsername(null)
      setAuthInfoAbortController(null)
    }
  }

  const checkUserInactiveStatus = async (email: string): Promise<boolean> => {
    try {
      notification.destroy('USER_INACTIVE')
      await axios.post(`${process.env.REACT_APP_API_URL}/core/auth-info`, { email })
      return true
    } catch (err) {
      if (err?.response?.data?.code === 'EmailPlatformUserInactive') {
        notification.error({
          message: formatMessage({ id: 'error.signin.inactiveUser' }),
          key: 'USER_INACTIVE',
          description: formatMessage({ id: 'error.auth.companyExists.line1' }),
          duration: 0,
        })
        return false
      }
      return true
    }
  }

  const getSlackTokens = () => {
    setSigninInProgress(true)
    slackAuth.signin(
      SLACK_REQUIRED_BOT_SCOPES,
      SLACK_REQUIRED_USER_SCOPES,
      slackTeamId
    ).then(async (response) => {
      setUserWorkspaceName(response.team.name)
      
      let userData: ICheckUserId | undefined = undefined
      const slackUserToken = response.authed_user.access_token as string
      const botToken: string | undefined = response.access_token
      try {
        slackAuth.setBotToken(botToken)
        slackAuth.setUserToken(slackUserToken)
        if (authUser.id) {
          await updateSlackToken(slackUserToken, botToken)
        }
        setShowSlackLoginRequiredModal(false)
        let userId = localStorage.getItem('userId') as string
        if (!userId) {
          userData = await getSlackUserId(response.authed_user.id, slackUserToken)
          userId = userData.vacationTrackerId as string
        }
        dispatch(setUserId(userId))
        if (!authUser.id) {
          await authSigin(userId, slackUserToken, 'slack', botToken)
        } else {
          dispatch(setAuthState(AuthStateEnum.signedIn))
        }
        setSigninInProgress(false)
      } catch(err) {
        if (userData) {
          handleUserData(userData, 'slack', {
            teamName: response.team.name,
            slackBotToken: botToken,
            slackUserToken: slackUserToken,
            slackOrgTeamId: response.enterprise?.id,
            slackBotId: response.bot_user_id,
            isOwner: userData?.slackUser?.isOwner,
            isAdmin: userData?.slackUser?.isAdmin,
            slackTeamId: userData?.slackUser?.slackTeamId,
            imageUrl: userData?.slackUser?.imageUrl,
            slackUserId: userData?.slackUser?.slackUserId,
            timezone: userData?.slackUser?.timezone,
          })
          setSigninInProgress(false)
        }

        if (err?.code === 'NotAuthorizedException' && (userData?.contactEmail || userData?.adminContacts)) {
          setCompanyExists(true)
          setContactAdminModal({
            platform: 'slack',
            organizationName: userData?.organizationName || '',
            contactEmail: userData?.contactEmail || '',
            adminContacts: userData?.adminContacts || [],
          })
          track('ORG_ALREADY_EXISTS_PAGE_VIEWED', { 
            ...omit(userData || {}, ['adminContacts', 'slackUser']),
            platform: 'slack', 
          })
          setSigninInProgress(false)
          return
        }
      }
    }).catch(error => {
      setSigninInProgress(false)
      notification.error({
        message: formatMessage({ id: 'error.notificationGeneral' }),
        description: `Slack error: ${error?.error}${error.needed ? ` ${error.needed}` : ''}`,
        duration: 0,
      })
    })
  }

  const authSigin = async (username: string, token: string, platform: string, botToken?: string) => {
    const signInResponse = await signin(username) as IAuthNextStep
    if (signInResponse.challengeName === 'CUSTOM_CHALLENGE' && signInResponse.challengeParam?.question === 'token') {
      const cognitoResponse = await confirmSigninWithCustomChallengeAnswer(token, { loginType: platform })
      localStorage.setItem('userId', cognitoResponse.username)

      try {
        const authToken = await getAuthTokens()
        if (gqlClient && authToken.idToken) {
          gqlClient.setHeader('Authorization', authToken.idToken)
        }
      } catch (error) {
        // Swallow the error
      }

      if (platform === 'slack') {
        try {
          await updateSlackToken(token, botToken)
        } catch (error) {
          if(error?.response?.data?.error && (error?.response.data.error === 'error.botTokenNotExist' || error?.response.data.error === 'error.botTokenNotValid')) {
            setSigninInProgress(false)
            setShowSlackLoginRequiredModal(true)
            return
          }
        }
      }
      dispatch(setUserId(cognitoResponse.username))
      await getCompanyAndUser(cognitoResponse.username, getCompanyAndUserInfoQuery, dispatch, history)
      track('LOGIN_COMPLETED', {
        platform,
        timestamp: new Date().toDateString(),
        source: 'app',
        status: 'completed',
      })
      dispatch(setAuthState(AuthStateEnum.signedIn))
    }
  }

  const goToSignup = () => {
    setSigninInProgress(false)
    dispatch(setAuthState(AuthStateEnum.signUp))
    history.push(FrontendUrls.signup)
  }

  const goToDashboard = (redirectUrl: string | FrontendUrls) => {
    setSigninInProgress(false)
    history.push(redirectUrl)
  }

  const handleUserData = (userData: ICheckUserId, platform, otherCreateUserData) => {
    setUserEmail(userData.mail || '')
    if(userData.type === 'USER_NOT_FOUND' && userData.subscriptionStatus === 'canceled') {
      notification.error({
        message: formatMessage({ id: 'connect.subscriptionExpiredTitle' }),
        description: formatMessage({ id: 'error.subscriptionExpired' }),
        duration: 0,
      })
      return
    }

    setSigninInProgress(false)
  
    if(userData.type === 'USER_NOT_FOUND') {
      setContactAdminModal({
        platform,
        organizationName: userData.organizationName || '',
        contactEmail: userData.contactEmail || '',
        adminContacts: userData.adminContacts || [],
      })
      track('ORG_ALREADY_EXISTS_PAGE_VIEWED', { 
        ...omit(userData || {}, ['adminContacts', 'slackUser']),
        platform, 
      })
      setCompanyExists(true)
      return
    }

    if (userData.type === 'COMPANY_NOT_FOUND') {
      localStorage.setItem('vtCreateUser', JSON.stringify({
        name: userData.name,
        id: `${platform}-${userData.id}`,
        mail: userData.mail,
        platform,
        ...otherCreateUserData,
      }))

      if (userData.existsOnAnotherPlatform && userData.existingOrganizationName) {
        track('ORG_EXISTS_ON_ANOTHER_PLATFORM_OPEN_MODAL', { platform, existingOrganizationName: userData.existingOrganizationName })
        setExistsOnAnotherPlatform(userData.existsOnAnotherPlatform)
        setCompanyAlreadySignedUpShowModal(true)
        return
      }
      showCreateAccountModal(true)
    }
  }

  const handleSigninError = (error, platform) => {
    setSigninInProgress(false)
    let errorMessage = (typeof error === 'object' && !(error instanceof Error)) ? (
      error.error ? error.error : JSON.stringify(error, null, 2)
    ) : (
      typeof error === 'string' ? error : error.toString()
    )
    let platformText
    if (platform === 'microsoft') {
      track('CONNECT_WITH_MICROSOFT_ERROR', { errorMessage, platform })
      platformText = 'Microsoft Teams'
    } else if (platform === 'google') {
      track('CONNECT_WITH_GOOGLE_ERROR', { errorMessage, platform })
      platformText = 'Google'
    } else if (platform === 'slack') {
      track('CONNECT_WITH_SLACK_ERROR', { errorMessage, platform })
      platformText = 'Slack'
    } else if (platform === 'adp') {
      track('CONNECT_WITH_ADP_ERROR', { errorMessage, platform })
      errorMessage = formatMessage({ id: 'connect.platformErrorDescription' })
      platformText = 'ADP'
    }

    // This can turn out to be a switch statement if we encounter more error cases
    // that require different actions/descriptions
    if (errorMessage.includes('interaction_required') || errorMessage.includes('invalid_grant')) {
      notification.error({
        message: formatMessage({ id: 'connect.interactionRequiredMSErrorTitle' }),
        description: <div>
          <Paragraph>{ formatMessage({ id: 'connect.interactionRequiredMSErrorDescription1' }) }</Paragraph>
          <Paragraph>{ formatMessage({ id: 'connect.interactionRequiredMSErrorDescription2' }) }</Paragraph>
          <Paragraph keyboard copyable>{ errorMessage }</Paragraph>
        </div>,
        duration: 0,
        btn: <Button onClick={() => connectWithMicrosoft(true)}>{ formatMessage({ id: 'connect.login' }) }</Button>,
      })
    } else {
      notification.error({
        message: formatMessage({ id: 'connect.platformErrorTitle' }),
        description: <div>
          <Paragraph>{ formatMessage({ id: 'connect.platformErrorDescription1' }) }</Paragraph>
          <Paragraph keyboard copyable>{ errorMessage }</Paragraph>
          <Paragraph>{ formatMessage({ id: 'connect.platformErrorDescription2' }, { platform: platformText }) }</Paragraph>
        </div>,
        duration: 0,
      })
      // Do not track Slack "access_denied" in Sentry, as it's handled above
      if (!errorMessage.includes('access_denied')) {
        Sentry.captureException(error)
      }
    }
  }

  const connectWithSlack = () => {
    const platform = 'slack'
    setSigninInProgress(true)
    track('THIRD_PARTY_LOGIN_STARTED', { platform, page: 'login' })
    track('CONNECT_WITH_SLACK_BUTTON_CLICKED', { platform })
    track('LOGIN_STARTED', {
      platform,
      timestamp: new Date().toISOString(),
      source: 'app',
      status: 'started',
    })
    slackAuth.connect()
      .then(async (response: ISlackOpenidTokenResponse) => {
        track('THIRD_PARTY_LOGIN_SUCCESS', { platform, page: 'login' })
        const slackUserToken: string | undefined = response.authed_user.access_token
        if (!slackUserToken) {
          throw new Error('Slack user token is required')
        }
        setSlackTeamId(response.team.id)
        slackAuth.setUserToken(slackUserToken)
        let userData: ICheckUserId | undefined = undefined
        try {
          userData = await getSlackUserId(response.authed_user.id, slackUserToken)
          if (userData.vacationTrackerId) {
            if (userData.mail) {
              const isActive = await checkUserInactiveStatus(userData.mail)
              if (!isActive) {
                setSigninInProgress(false)
                return
              }
            }
            localStorage.setItem('userId', userData.vacationTrackerId)
            let botToken: string | undefined
            try {
              botToken = slackAuth.getBotToken()
            } catch (error) {
              // Bot token is not available, but we can still proceed
            }
            const areScopesValid = await slackAuth.areScopesValid({
              userToken: slackUserToken,
              requiredUserScopes: SLACK_REQUIRED_USER_SCOPES,
              botToken: botToken,
              requiredBotScopes: SLACK_REQUIRED_BOT_SCOPES,
            })
            if (!areScopesValid) {
              throw 'missing_scope'
            }
            await authSigin(userData.vacationTrackerId, slackUserToken, platform, botToken)
          } else {
            throw 'Handle user data'
          }
        } catch (error) {
          if (error === 'missing_scope' || error?.response?.data === 'missing_scope') {
            setShowSlackLoginRequiredModal(true)
            setSigninInProgress(false)
            return
          }

          // If not found vacationTrackerId, but we have userData, we can use it to show warning modal
          if (userData) {
            handleUserData(userData, platform, {
              teamName: null,
              slackBotToken: null,
              slackUserToken: slackUserToken,
              slackOrgTeamId: response.enterprise?.id,
              slackBotId: null,
              isOwner: userData?.slackUser?.isOwner,
              isAdmin: userData?.slackUser?.isAdmin,
              slackTeamId: userData?.slackUser?.slackTeamId,
              imageUrl: userData?.slackUser?.imageUrl,
              slackUserId: userData?.slackUser?.slackUserId,
              timezone: userData?.slackUser?.timezone,
            })
          }

          if (error?.code === 'NotAuthorizedException' && (userData?.contactEmail || userData?.adminContacts)) {
            setContactAdminModal({
              platform,
              organizationName: userData?.organizationName || '',
              contactEmail: userData?.contactEmail || '',
              adminContacts: userData?.adminContacts || [],
            })
            track('ORG_ALREADY_EXISTS_PAGE_VIEWED', { 
              ...omit(userData || {}, ['adminContacts', 'slackUser']),
              platform, 
            })
            setCompanyExists(true)
            setSigninInProgress(false)
            return
          }
        }
      })
      .catch(e => {
        track('THIRD_PARTY_LOGIN_FAILED', { platform, error: e, page: 'login' })
        handleSigninError(e, platform)
      })
  }

  const connectWithADP = async() => {

    try {
      await form.validateFields(['email'])
    } catch (error) {
      return
    }

    setSigninInProgress(true)
    
    let userId = null
    try {
      const userData = await axios.post(`${process.env.REACT_APP_API_URL}/core/auth-info`, {
        email: userEmail as string,
      })
      userId = userData.data.username
      const adpLoginEnabled = userData.data?.loginOptions?.includes('adp')
      if (!adpLoginEnabled) {
        throw new Error('ADP_LOGIN_NOT_ENABLED')
      }
      setAdpLoginEnabled(Boolean(adpLoginEnabled))
    } catch (error) {
      setSigninInProgress(false)
      setAdpLoginEnabled(false)
      handleSigninError(error, 'adp')
      return
    }

    const platform = 'adp'
    track('THIRD_PARTY_LOGIN_STARTED', { platform, page: 'login' })
    track('CONNECT_WITH_ADP_BUTTON_CLICKED', { platform })
    track('LOGIN_STARTED', {
      platform,
      timestamp: new Date().toISOString(),
      source: 'app',
      status: 'started',
    })

    const adpAuthorizationUrl = 'https://accounts.adp.com/auth/oauth/v2/authorize?' + 
      'response_type=code&' +
      `client_id=${process.env.REACT_APP_ADP_CLIENT_ID_SSO}&` +
      `redirect_uri=${process.env.REACT_APP_DASHBOARD_URL}/signin&` +
      'scope=openid%20profile'

    const adpConsentUrl = 'https://adpapps.adp.com/consent-manager/pending/direct?' +
      `consumerApplicationID=${process.env.REACT_APP_ADP_CONSUMER_APPLICATION_ID}&` +
      `successUri=${encodeURIComponent(adpAuthorizationUrl)}`

    BrowserRuntime().openAndWaitForMessage(adpConsentUrl, 'adp-auth')
      .then(async (code) => {
        if (code && userId) {
          await authSigin(userId as string, code as string, platform)
          setSigninInProgress(false)
          setAdpLoginEnabled(false)
        }
      })
      .catch(error => {
        setSigninInProgress(false)
        setAdpLoginEnabled(false)
        handleSigninError(error, platform)
      })
  }

  const connectWithMicrosoft = (interactionRequired = false) => {
    const platform = 'microsoft'
    setSigninInProgress(true)
    track('THIRD_PARTY_LOGIN_STARTED', { platform, page: 'login' })
    track('CONNECT_WITH_MICROSOFT_BUTTON_CLICKED', { platform })
    track('LOGIN_STARTED', {
      platform,
      timestamp: new Date().toISOString(),
      source: 'app',
      status: 'started',
    })
    // This requires promises, not async/await because window.open requires sync not async flow
    msAuth.signin(['user.readbasic.all', 'team.readbasic.all'], interactionRequired)
      .then(async ([tokens, msUser, tenantId]) => {
        track('THIRD_PARTY_LOGIN_SUCCESS', { platform, page: 'login' })
        const userData: ICheckUserId = await getMsUserId(tenantId, tokens.accessToken)
        logger.info('userData', userData)
        if (userData.vacationTrackerId && userData.mail) {
          const isActive = await checkUserInactiveStatus(userData.mail)
          if (!isActive) {
            setSigninInProgress(false)
            return
          }
        }
        if (!userData.id) {
          msAuth.setTokens(tokens)
          handleUserData(userData, platform, { tenantId, msUserId: userData.id })
        }
        try {
          await authSigin(userData.id as string, tokens.accessToken, platform)
        } catch (error) {
          msAuth.setTokens(tokens)
          handleUserData(userData, platform, { tenantId, msUserId: userData.id })
        }
      })
      .catch(e => handleSigninError(e, platform))
  }

  const connectWithGoogle = () => {
    const platform = 'google'
    track('THIRD_PARTY_LOGIN_STARTED', { platform, page: 'login' })
    track('LOGIN_PAGE_LOGIN_WITH_GOOGLE_BUTTON_CLICKED', { platform })
    track('LOGIN_STARTED', {
      platform,
      timestamp: new Date().toISOString(),
      source: 'app',
      status: 'started',
    })
    setSigninInProgress(true)
    googleAuth.signin()
      .then(async () => {
        track('THIRD_PARTY_LOGIN_SUCCESS', { platform, page: 'login' })
        const googleUser = googleAuth.getSignedInUser()
        if (!googleUser.hd) {
          notification.error({
            message: formatMessage({ id: 'connect.google.notWorkspaceUserTitle' }),
            description: formatMessage({ id: 'connect.google.notWorkspaceUserDescription' }),
            duration: 0,
          })
          return
        }
        const userData: ICheckUserId = await getGoogleUserId(googleAuth.getIdToken())
        logger.info('userData', userData)
        if (userData.vacationTrackerId && userData.mail) {
          const isActive = await checkUserInactiveStatus(userData.mail)
          if (!isActive) {
            setSigninInProgress(false)
            return
          }
        }
        try {
          await authSigin(userData.vacationTrackerId as string, googleAuth.getAccessToken() , platform)
        } catch (error) {
          handleUserData(userData, platform, {
            googleDomain: googleUser.hd,
            imageUrl: googleUser.picture,
            googleUserId: googleUser.sub,
          })
        }
      })
      .catch(e => handleSigninError(e, platform))
  }

  const onEnterMFACode = async (code: string) => {
    try {
      const cognitoResponse = await confirmSigninWithCustomChallengeAnswer(code)

      localStorage.setItem('userId', cognitoResponse.username)
      dispatch(setUserId(cognitoResponse.username))
      await getCompanyAndUser(cognitoResponse.username, getCompanyAndUserInfoQuery, dispatch, history)
      track('LOGIN_COMPLETED', {
        focusedPlatform,
        timestamp: new Date().toDateString(),
        source: 'app',
        status: 'completed',
      })
      dispatch(setAuthState(AuthStateEnum.signedIn))
    } catch (e) {
      notification.error({ 
        message: formatMessage({ id: 'app.mfaLoginFailed' }),
        description: formatMessage({ id: getCognitoErrors(e.code as string) }),
        duration: 10,
      })
      setShowMFAModal(false)
      Sentry.captureException(e)
    }
  }

  const connectWithMail = async () => {
    try {
      const values = await form.validateFields()
      if (!values.password) {
        await getUsersByEmail(values.email)
        setShowPasswordInput(true)
        return
      }
      setSigninInProgress(true)
      track('LOGIN_PAGE_LOGIN_WITH_EMAIL_AND_PASSWORD_BUTTON_CLICKED', { platform: 'email' })
      const cognitoResponse = await signin(username || '', values.password)
      if (['SMS_MFA', 'SOFTWARE_TOKEN_MFA'].includes((cognitoResponse as IAuthNextStep).challengeName as string)) {
        setShowMFAModal(true)
        return
      } else if ((cognitoResponse as IAuthNextStep).challengeName === 'NEW_PASSWORD_REQUIRED') {
        // Ask for the new password, then do this:
        // const loggedUser = await Auth.completeNewPassword(
        //   cognitoResponse, // the Cognito User Object
        //   newPassword, // the new password
        // )
      } else if ((cognitoResponse as IAuthNextStep).challengeName === 'MFA_SETUP') {
        // This happens when the MFA method is TOTP
        // The user needs to setup the TOTP before using it
        // More info please check the Enabling MFA part
        // Auth.setupTOTP(cognitoResponse)
      }
      localStorage.setItem('userId', (cognitoResponse as IAuthUser).username)

      dispatch(setUserId((cognitoResponse as IAuthUser).username))
      await getCompanyAndUser((cognitoResponse as IAuthUser).username, getCompanyAndUserInfoQuery, dispatch, history)
      track('LOGIN_COMPLETED', {
        platform: 'email',
        timestamp: new Date().toDateString(),
        source: 'app',
        status: 'completed',
      })
      dispatch(setAuthState(AuthStateEnum.signedIn))
    } catch(err) {
      setSigninInProgress(false)
      if (err.name === 'NotAuthorizedException') {
        notification.error({
          message: formatMessage({ id: 'error.incorrectPassword' }),
          key: 'INCORRECT_PASSWORD',
          description: formatMessage({ id: 'error.incorrectPasswordMessage' }),
          btn: (<Button onClick={() => {
            showForgotPasswordModal(true)
            notification.destroy('INCORRECT_PASSWORD')
          }}>{ formatMessage({ id: 'app.resetPassword' }) }</Button>),
          duration: 0,
        })
        return
      } else if (err.name === 'UserNotConfirmedException') {
        // The error happens if the user didn't finish the confirmation step when signing up
        // In this case you need to resend the code and confirm the user
        // About how to resend the code and confirm the user, please check the signUp part
      } else if (err.name === 'PasswordResetRequiredException') {
        // The error happens when the password is reset in the Cognito console
        // In this case you need to call forgotPassword to reset the password
        // Please check the Forgot Password part.
      } else if (err.name === 'UserNotFoundException') {
        // The error happens when the supplied username/email does not exist in the Cognito user pool
      }
      Sentry.captureException(err)
    }
  }

  const connectWithPlatform = (platform: string) => {
    track('ORG_EXISTS_ON_ANOTHER_PLATFORM_CHOOSE_EXISTING', { platform })
    localStorage.removeItem('createUser')
    setCompanyAlreadySignedUpShowModal(true)
    if (platform === 'slack') {
      connectWithSlack()
    } else if (platform === 'microsoft') {
      connectWithMicrosoft()
    } else if (platform === 'google') {
      connectWithGoogle()
    }
  }

  // This happens when the validation is not finished and the form is submitted
  // Or in case there are errors in the form
  const onFormFinishFailed = async ({ errorFields, outOfDate }) => {
    try {
      // If there are no errors, but the form is out of date, we need to validate the fields
      // and resubmit the form (to avoid the double-click)
      if (errorFields.length === 0 && outOfDate === true) {
        await form.validateFields()
        form.submit()
      }
      // Otherwise, we need to show the error message (we'll do that in the catch block)
      throw new Error('Validation failed')
    } catch (err) {
      // If the validation fails, we need to show the error message
      // and focus the first field with an error
      if (err.errorFields && err.errorFields.length > 0) {
        const firstError = err.errorFields[0]
        const errorField = firstError.name[0]
        const field = form.getFieldInstance(errorField)
        if (field) {
          field.focus()
        }
      }
    }
  }

  const onFormFieldsChange = (changedFields: FieldData[]) => {
    const emailField = changedFields && changedFields.find(field => Array.isArray(field.name) && field.name.includes('email'))
    if (!emailField?.errors || emailField.errors.length === 0) {
      setUserEmail(emailField?.value as string)
    }
    const password = changedFields && changedFields.find(field => Array.isArray(field.name) && field.name.includes('password'))
    setPasswordRequired(changedFields.length === 0 || password?.value === '')
  }

  return (
    <div className="auth-wrap">
      <SeoTags
        title='connect.meta.title.signin'
        description='connect.meta.description'
      />
      <div className="auth-container" style={{ height: '100vh' }}>
        <Row style={{ height: '100%' }}>
          <Col span={12} className="auth-sidebar-info">
            <MobileApp />
          </Col>
          <Col span={12} className="auth-sidebar-form">
            {query?.get('adpLogin') ? (
              <>
                <Title level={3}><IntlMessages id='signin.adpLoginTitle' defaultMessage='Sign in with ADP' /></Title>
                <Paragraph className="auth-sidebar-info-text">
                  <IntlMessages id='signin.adpLoginDescription' defaultMessage='Enter your work email address and click "Connect with ADP" to sign in through your ADP account.' />
                </Paragraph>
              </>
            ) : (
              <>
                <Title level={3}><IntlMessages id='signin.goodToSeeYouAgain' /></Title>
                <Paragraph className="auth-sidebar-info-text"><IntlMessages id='signin.loginDescription' /></Paragraph>
              </>
            )}
            <Spin spinning={signinInProgress}>
              <div className="auth-sidebar-content">
                {!showPasswordInput &&
                  <>
                    {!query?.get('adpLogin') && (!focusedPlatform || focusedPlatform === PlatformEnum.Microsoft) &&
                      <Button icon={<TeamsIcon />} data-qa="slack-connect" block onClick={() => connectWithMicrosoft()}>
                        <IntlMessages id="createCompany.connectWithMicrosoft" />
                      </Button>
                    }
                    {!query?.get('adpLogin') && (!focusedPlatform || focusedPlatform === PlatformEnum.Google) &&
                      <Button icon={<GoogleIcon />} size='large' data-qa="google-connect" className="vt-auth" block onClick={() => connectWithGoogle()}>
                        <IntlMessages id="createCompany.connectWithGoogle" />
                      </Button>
                    }
                    {!query?.get('adpLogin') && (!focusedPlatform || focusedPlatform === PlatformEnum.Slack) &&
                      <Button icon={<SlackIcon />} data-qa="slack-connect" block onClick={() => connectWithSlack()}>
                        <IntlMessages id="createCompany.connectWithSlack" />
                      </Button>
                    }
                    {!query?.get('adpLogin') && !focusedPlatform &&
                      <Divider>{ formatMessage({id: 'app.or'}).toLowerCase() }</Divider>
                    }
                  </>
                }
                {(!focusedPlatform || focusedPlatform === PlatformEnum.Email) &&
                  <>
                    {query?.get('adpLogin') ? (
                      <div style={{ marginBottom: '16px' }}>
                        <Paragraph className="connect-with-email" style={{ marginBottom: '8px', fontWeight: 600 }}>
                          <IntlMessages id="signin.adpStep1" defaultMessage="Step 1: Enter your email" />
                        </Paragraph>
                        <Paragraph style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '0' }}>
                          <IntlMessages id="signin.adpStep1Description" defaultMessage="Use the same email address associated with your ADP account" />
                        </Paragraph>
                      </div>
                    ) : (
                      <>
                        {!showPasswordInput &&
                          <Paragraph className="connect-with-email"><IntlMessages id="app.email" /></Paragraph>
                        }
                        {showPasswordInput &&
                          <Paragraph className="connect-with-email"><IntlMessages id="component.setPassword.enterPassword" /></Paragraph>
                        }
                      </>
                    )}
                   
                    <Form
                      name="connect-with-email"
                      form={form}
                      layout="horizontal"
                      onFinish={connectWithMail}
                      onFinishFailed={onFormFinishFailed}
                      onFieldsChange={onFormFieldsChange}
                    >
                      {!showPasswordInput &&
                        <Form.Item
                          name="email"
                          style={{ width: '100%' }}
                          rules={[{ required: true, type: 'email', message: (<IntlMessages id="connect.pleaseInputValidEmail" />) }]}
                        >
                          <Input
                            className="email-input data-hj-allow"
                            size="large"
                            data-qa='email-login-mail'
                            placeholder={query?.get('adpLogin') ? 
                              formatMessage({ id: 'app.enterWorkEmailForADP' }, { defaultMessage: 'Enter your ADP work email' }) : 
                              formatMessage({ id: 'app.enterWorkEmail' })
                            }
                            ref={emailInputRef}
                          />
                        </Form.Item>
                      }
                      { showPasswordInput &&
                        <Form.Item
                          name="password"
                          style={{ width: '100%' }}
                          rules={[{ required: true }]}
                        >
                          <Input.Password
                            size="large"
                            data-qa='email-login-password'
                            ref={passwordInputRef}
                            placeholder={formatMessage({ id: 'connect.inputPasswordPlaceholder' })}
                            autoComplete='current-password'
                          />
                        </Form.Item>
                      }
                      { !query?.get('adpLogin') &&
                        <Form.Item>
                          <Button
                            size="large"
                            type="primary"
                            htmlType="submit"
                            disabled={isCheckingEmail || passwordRequired || signinInProgress}
                            data-qa='email-login-submit'
                            block
                            loading={signinInProgress}
                          >
                            <IntlMessages id="createCompany.connectWithEmail" />
                          </Button>
                        </Form.Item>
                      }
                      { !query?.get('adpLogin') &&
                        <Link onClick={() => showForgotPasswordModal(true)}>
                          <IntlMessages id="connect.forgotYourPassword" />
                        </Link>
                      }
                      {showPasswordInput &&
                        <div style={{ marginTop: '16px'}}>
                          <Link onClick={() => setShowPasswordInput(false)}>
                            <IntlMessages id="connect.backToLoginWithOtherPlatofrm" />
                          </Link>
                        </div>
                      }
                      {(adpLoginEnabled || query?.get('adpLogin')) && (
                        <div>
                          {query?.get('adpLogin') && (
                            <div style={{ marginTop: '24px', marginBottom: '12px' }}>
                              <Paragraph style={{ marginBottom: '4px', fontWeight: 600, fontSize: '14px' }}>
                                <IntlMessages id="signin.adpStep2" defaultMessage="Step 2: Connect with ADP" />
                              </Paragraph>
                              <Paragraph style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '0' }}>
                                <IntlMessages id="signin.adpStep2Description" defaultMessage="You'll be redirected to ADP to complete the sign-in process" />
                              </Paragraph>
                            </div>
                          )}
                          <Button
                            size="large"
                            type="primary"
                            onClick={() => { connectWithADP() }}
                            block
                            style={{ marginTop: query?.get('adpLogin') ? '8px' : '16px' }}
                            loading={signinInProgress}
                            disabled={!userEmail || signinInProgress}
                          >
                            <IntlMessages id="createCompany.connectWithADP" />
                          </Button>
                        </div>
                      )}
                    </Form>
                  </>
                }
              </div>
            </Spin>
            {!query?.get('adpLogin') && (
              <Paragraph className="auth-sidebar-login">
                <IntlMessages id="connect.dontHaveAnAccount" />&nbsp;
                <Link onClick={() => history.push(FrontendUrls.signup)}>
                  <IntlMessages id="user.signUp" />
                </Link>
              </Paragraph>
            )}
          </Col>
        </Row>
      </div>

      {companyExists && contactAdminModal && userEmail &&
        <CompanyExistsModal
          companyExists={companyExists}
          setCompanyExists={setCompanyExists}
          contactAdminModal={contactAdminModal}
          userEmail={userEmail}
          userWorkspaceName={userWorkspaceName}
        />
      }

      { showSlackLoginRequiredModal &&
        <Modal
          title={formatMessage({ id: 'error.slack.usersLoginRequiredTitle' })}
          open={showSlackLoginRequiredModal}
          onOk={getSlackTokens}
          cancelText={formatMessage({ id: 'app.cancel'})}
          okText={formatMessage({ id: 'connect.slack.usersLoginRequiredButton'})}
          onCancel={() => {
            dispatch(setUserId(localStorage.getItem('userId') as string))
            setShowSlackLoginRequiredModal(false)
          }}
        >
          <p><IntlMessages id="connect.slack.usersLoginRequiredDescription1" /></p>
        </Modal>
      }

      { companyAlreadySignedUpShowModal &&
        <CompanyAlreadySignedUpModal
          showModal={companyAlreadySignedUpShowModal}
          existsOnAnotherPlatform={existsOnAnotherPlatform}
          connectWithPlatform={connectWithPlatform}
          handleCloseCompanyAlreadySignedUpModal={() => setCompanyAlreadySignedUpShowModal(false)}
        />
      }

      { createAccountModalVisible &&
        <DontHaveAnAccountModal
          showCreateAccountModal={showCreateAccountModal}
          createAccountModalVisible={createAccountModalVisible}
          goToSignup={goToSignup}
        />
      }

      { forgotPasswordModalVisible &&
        <ForgotPasswordModal
          forgotPasswordModalVisible={forgotPasswordModalVisible}
          showForgotPasswordModal={showForgotPasswordModal}
        />
      }

      { showMFAModal && <MFACodeModal onSave={onEnterMFACode} />}
    </div>
  )
}

export default Signin